import { HttpClientModule } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { GlobalErrorService } from '@app/error-handling/global.error.service';
import { AssetSummaryModule } from '@features/asset-summary/asset-summary.module';
import { AuthEffects } from '@features/auth/auth.effects';
import { AuthModule } from '@features/auth/auth.module';
import { ClientStateGuard } from '@features/client-state/client-state-guard';
import { clientStateReducer } from '@features/client-state/client-state-reducer';
import { FeedbackModule } from '@features/feedback/feedback.module';
import { ConsoleLoggerService } from '@features/logging/console.logger.service';
import { LoggerService } from '@features/logging/logger.service';
import { NavbarModule } from '@features/navbar/navbar.module';
import { OrderEffects } from '@features/orders/order.effects';
import { OrdersServicesModule } from '@features/orders/orders-services.module';
import { NotFoundModule } from '@features/shared/notfound/not-found.module';
import { StateModule } from '@features/state/state.module';
import { TagManagerModule } from '@features/tag-manager/tag-manager.module';
import { TermsOfUseAgreementRepository } from '@features/terms-of-use/terms-of-use-agreement-repository';
import { TermsOfUseGuard } from '@features/terms-of-use/terms-of-use-guard';
import { UserExpirationAlertModule } from '@features/user-expiration-alert/user-expiration-alert.module';
import { ScrollModule } from '@lib/scroll/scroll.module';
import { EffectsModule } from '@ngrx/effects';
import { routerReducer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { INITIAL_STATE, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { SimpleNotificationsModule } from 'angular2-notifications';
import { ApplicationInsightsModule } from 'app/application-insights/application-insights.module';
import * as LogRocket from 'logrocket';
import createNgrxMiddleWare from 'logrocket-ngrx';
import { AppEffects } from './app-effects';
import { AppLayoutModule } from './app-layout/app-layout.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ChunkErrorHandler } from './error-handling/chunk-error-handler';
import { ClientStateService } from './features/client-state/client-state.service';
import { CollectionEffects, collectionReducer } from './features/collections';
import { filtersReducer } from './features/filters/filters.reducer';
import { orderReducer } from './features/orders';
import { searchReducer } from './features/search/search.reducer';
import { SharedModule } from './features/shared/shared.module';
import { IMCC_HTTP_INTERCEPTORS } from './imcc-http-interceptors';
import { loadInitialState } from './load-initial-state';
import { LoadingAppModule } from './loading-app/loading-app.module';
import { resetStateMetaReducer } from './reset-state.meta-reducer';

@NgModule({
  declarations: [AppComponent],
  imports: [
    AuthModule,
    AppLayoutModule,
    AssetSummaryModule,
    LoadingAppModule,
    ApplicationInsightsModule,
    BrowserModule,
    EffectsModule.forRoot([
      AppEffects,
      CollectionEffects,
      OrderEffects,
      AuthEffects,
    ]),
    FeedbackModule,
    HttpClientModule,
    NotFoundModule,
    OrdersServicesModule,
    StoreModule.forRoot(
      {
        search: searchReducer,
        filters: filtersReducer,
        collection: collectionReducer,
        orders: orderReducer,
        router: routerReducer,
        clientState: clientStateReducer,
      },
      {
        metaReducers: [resetStateMetaReducer, createNgrxMiddleWare(LogRocket, {})],
      },
    ),
    SharedModule,
    StoreRouterConnectingModule.forRoot(),
    StoreDevtoolsModule.instrument({ maxAge: 50 }),
    AppRoutingModule,
    AuthModule,
    ScrollModule,
    StateModule,
    BrowserAnimationsModule,
    SimpleNotificationsModule.forRoot(),
    NavbarModule,
    UserExpirationAlertModule,
    TagManagerModule,
  ],
  providers: [
    ...IMCC_HTTP_INTERCEPTORS,
    ChunkErrorHandler,
    ClientStateService,
    ClientStateGuard,
    { provide: LoggerService, useClass: ConsoleLoggerService },
    TermsOfUseGuard,
    TermsOfUseAgreementRepository,
    {
      provide: INITIAL_STATE,
      useFactory: () => loadInitialState(),
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorService,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
